import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MousePointer2, TrendingUp, DollarSign, BarChart3, 
  Download, RefreshCw, Calendar, Users, ExternalLink,
  Plus, Package, Edit2, Trash2, Save, X, Check, AlertCircle,
  Plane, Bus, ChevronDown, ChevronUp, Image, Globe, Play, Activity,
  Search, MapPin, Upload, Database, Layers, ChevronRight, ChevronLeft,
  FileText, Eye, Link as LinkIcon, Image as ImageIcon, Loader2, Copy, Star,
  CheckCircle, XCircle, Clock, Music, Tag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ConcertForm from '../components/ConcertForm';
import { PackageForm, EMPTY_PACKAGE } from '../components/admin/PackageForm';
import { PackageRow } from '../components/admin/PackageRow';
import AnalyticsPanel from '../components/admin/AnalyticsPanel';
import EventCategoriesManager from '../components/admin/EventCategoriesManager';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CSRF token helper
const getCSRFToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

// Token refresh helper - server rotates cookie, no localStorage write
const refreshAccessToken = async () => {
  try {
    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include'  // Send refresh_token cookie + receive new access_token cookie
    });
    
    if (response.ok) {
      // Server rotates the httpOnly access_token cookie automatically
      if (process.env.NODE_ENV === 'development') console.log('[Auth] Token refreshed (cookie-based)');
      return true;
    }
    return false;
  } catch (error) {
    console.error('[Auth] Token refresh failed:', error);
    return false;
  }
};

// Secure fetch helper - uses httpOnly cookie auth (credentials: include)
// Falls back to localStorage Bearer token for backward compatibility during migration
const secureFetch = async (url, options = {}, isRetry = false) => {
  const method = options.method?.toUpperCase() || 'GET';
  const headers = { ...options.headers };
  
  // Backward-compat: if token still in localStorage (legacy users), send as Bearer.
  // Backend prefers httpOnly cookie when both are present.
  const legacyToken = localStorage.getItem('token');
  if (legacyToken && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${legacyToken}`;
  }
  
  // Add CSRF token for state-changing requests
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
  }
  
  // credentials: 'include' sends httpOnly access_token cookie automatically
  const response = await fetch(url, { ...options, headers, credentials: 'include' });
  
  // Handle token expired - attempt refresh and retry once
  if (response.status === 401 && !isRetry) {
    const errorData = await response.clone().json().catch(() => ({}));
    if (errorData.detail === 'token_expired' || errorData.detail === 'Token expired') {
      if (process.env.NODE_ENV === 'development') console.log('[SecureFetch] Token expired, attempting refresh...');
      const refreshed = await refreshAccessToken();
      
      if (refreshed) {
        // After refresh, server has rotated the httpOnly cookie. Retry with cookie auth.
        const retryHeaders = { ...options.headers };
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
          const csrfToken = getCSRFToken();
          if (csrfToken) {
            retryHeaders['X-CSRF-Token'] = csrfToken;
          }
        }
        if (process.env.NODE_ENV === 'development') console.log('[SecureFetch] Retrying with new cookie...');
        return fetch(url, { ...options, headers: retryHeaders, credentials: 'include' });
      } else {
        // Refresh failed - clear local state and redirect to login
        if (process.env.NODE_ENV === 'development') console.log('[SecureFetch] Refresh failed, logging out...');
        localStorage.removeItem('token');  // Clean up legacy token if present
        localStorage.removeItem('user');
        window.location.href = '/';
        return response;
      }
    }
  }
  
  // Handle CSRF errors
  if (response.status === 403) {
    const errorData = await response.clone().json().catch(() => ({}));
    if (errorData.detail?.includes('CSRF')) {
      console.error('[SecureFetch] CSRF Error:', errorData.detail);
      if (window.confirm('Sesiunea de securitate a expirat. Dorești să reîmprospătezi pagina?')) {
        window.location.reload();
      }
    }
  }
  
  return response;
};

const COLORS = ['#D4AF37', '#40C4FF', '#10B981', '#EF4444', '#8B5CF6', '#F59E0B', '#EC4899', '#6366F1'];

// Categories and Operators data
const CATEGORIES = [
  'Circuite Externe', 'Circuite Romania', 'City Break', 'Concerte'
];

const OPERATORS = [
  'Paralela 45', 'Hello Holidays', 'Christian Tour', 'Eximtur', 'Eturia', 
  'Cocktail Holidays', 'Coral Travel', 'Karpaten', 'Join UP!', 
  'TUI TravelCenter', 'Dertour', 'Eurolines'
];

// Continent and Country data for package form
const PACKAGES_PER_PAGE = 50;

export default function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentClicks, setRecentClicks] = useState([]);
  const [dateRange, setDateRange] = useState('30');
  const [user, setUser] = useState(null);
  
  // Package management state
  const [activeTab, setActiveTab] = useState('analytics'); // 'analytics' | 'packages'
  const [packages, setPackages] = useState([]);
  const [packagesTotal, setPackagesTotal] = useState(0);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showConcertFormAdmin, setShowConcertFormAdmin] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPackages, setSelectedPackages] = useState(new Set());
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [duplicatingPackageId, setDuplicatingPackageId] = useState(null);
  const [showBulkPrice, setShowBulkPrice] = useState(false);
  const [bulkPriceMode, setBulkPriceMode] = useState('percent'); // percent, fixed_add, fixed_set
  const [bulkPriceValue, setBulkPriceValue] = useState('');
  const [bulkPriceLoading, setBulkPriceLoading] = useState(false);
  
  // Migration (Export/Import) state
  const [migrationExporting, setMigrationExporting] = useState(false);
  const [migrationImporting, setMigrationImporting] = useState(false);
  const [selectedOperatorForExport, setSelectedOperatorForExport] = useState('all');
  
  // Geo (Continents/Countries) management state
  const [continents, setContinents] = useState([]);
  const [countries, setCountries] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [editingContinent, setEditingContinent] = useState(null);
  const [editingCountry, setEditingCountry] = useState(null);
  const [showContinentForm, setShowContinentForm] = useState(false);
  const [showCountryForm, setShowCountryForm] = useState(false);
  const [selectedContinentFilter, setSelectedContinentFilter] = useState('all');

  // Homepage Cards management state
  const [homepageCards, setHomepageCards] = useState([]);
  const [homepageCardsLoading, setHomepageCardsLoading] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [showCardForm, setShowCardForm] = useState(false);

  // Operator accounts management state
  const [operatorAccounts, setOperatorAccounts] = useState([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [newOperator, setNewOperator] = useState({ email: '', password: '', name: '', operator_name: '', can_publish_directly: false });
  const [pendingPackages, setPendingPackages] = useState([]);
  const [cardFormData, setCardFormData] = useState({
    title: '', slug: '', description: '', image_url: '', link_url: '', link_text: 'Vezi mai mult',
    display_order: 0, is_active: true, card_type: 'link', selected_package_ids: []
  });
  const [packageSearchQuery, setPackageSearchQuery] = useState('');
  const [packageSearchResults, setPackageSearchResults] = useState([]);
  const [packageSearchLoading, setPackageSearchLoading] = useState(false);

  // Tour Operators management state (legacy - for tour operator names, not accounts)
  const [operators, setOperators] = useState([]);
  const [showOperatorForm, setShowOperatorForm] = useState(false);
  const [editingOperator, setEditingOperator] = useState(null);
  const [newOperatorName, setNewOperatorName] = useState('');

  // Check auth and super admin status
  useEffect(() => {
    const checkAuth = async () => {
      const storedUser = localStorage.getItem('user');
      
      if (!storedUser) {
        toast.error('Autentificare necesară');
        navigate('/');
        return;
      }

      try {
        // Verify session via httpOnly cookie (auth_token sent automatically with credentials:'include')
        // Falls back to legacy Bearer token if user still has one in localStorage
        const legacyToken = localStorage.getItem('token');
        const headers = {};
        if (legacyToken) headers['Authorization'] = `Bearer ${legacyToken}`;
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers,
          credentials: 'include',
        });
        
        if (!response.ok) {
          throw new Error('Auth failed');
        }
        
        const currentUser = await response.json();
        
        if (!currentUser.is_super_admin) {
          // Redirect operators to their dashboard
          if (currentUser.role === 'operator') {
            navigate('/operator');
            return;
          }
          toast.error('Acces restricționat – doar pentru administratorul principal');
          navigate('/');
          return;
        }
        
        setUser(currentUser);
        loadData();
      } catch (error) {
        toast.error('Acces restricționat – doar pentru administratorul principal');
        navigate('/');
      }
    };

    checkAuth();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, clicksRes] = await Promise.all([
        secureFetch(`${API_URL}/api/dashboard/stats?days=${dateRange}`),
        secureFetch(`${API_URL}/api/dashboard/recent-clicks?days=${dateRange}&limit=100`)
      ]);

      if (!statsRes.ok || !clicksRes.ok) {
        throw new Error('Failed to load dashboard data');
      }

      const [statsData, clicksData] = await Promise.all([
        statsRes.json(),
        clicksRes.json()
      ]);

      setStats(statsData);
      setRecentClicks(clicksData.clicks || []);
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const loadPackages = async (page = currentPage, search = searchTerm) => {
    if (!user) return;
    
    setPackagesLoading(true);
    try {
      const offset = (page - 1) * PACKAGES_PER_PAGE;
      let url = `${API_URL}/api/admin/packages?limit=${PACKAGES_PER_PAGE}&offset=${offset}`;
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const response = await secureFetch(url);
      
      if (!response.ok) throw new Error('Failed to load packages');
      
      const data = await response.json();
      setPackages(data.packages || []);
      setPackagesTotal(data.total || 0);
    } catch (error) {
      console.error('Load packages error:', error);
      toast.error('Eroare la încărcarea pachetelor');
    } finally {
      setPackagesLoading(false);
    }
  };

  // Pagination helpers
  const totalPages = Math.ceil(packagesTotal / PACKAGES_PER_PAGE);
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      loadPackages(newPage, searchTerm);
    }
  };

  // Search handler with debounce effect
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Effect to trigger search when searchTerm changes
  useEffect(() => {
    if (user && activeTab === 'packages') {
      const timeoutId = setTimeout(() => {
        loadPackages(1, searchTerm);
      }, 300); // Debounce 300ms
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [dateRange, user]);

  useEffect(() => {
    if (activeTab === 'packages' && user) {
      loadPackages();
    }
  }, [activeTab, user]);

  const handleExport = async () => {
    try {
      const response = await secureFetch(`${API_URL}/api/dashboard/export?days=${dateRange}`);
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vaai_clicks_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export CSV descărcat cu succes');
    } catch (error) {
      toast.error('Eroare la export');
    }
  };

  const handleReset = async () => {
    // Confirm before resetting
    if (!confirm('Sigur vrei să resetezi toate statisticile la 0? Această acțiune va șterge istoricul de click-uri.')) {
      return;
    }
    
    try {
      const response = await secureFetch(`${API_URL}/api/dashboard/reset-stats`, {
        method: 'POST',
      });
      
      if (!response.ok) throw new Error('Reset failed');
      
      const data = await response.json();
      toast.success(data.message || 'Statistici resetate cu succes!');
      
      // Reload data to show zeros
      loadData();
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Eroare la resetarea statisticilor');
    }
  };

  const handleSavePackage = async (data) => {
    setFormLoading(true);
    
    try {
      const url = editingPackage?.id 
        ? `${API_URL}/api/admin/packages/${editingPackage.id}`
        : `${API_URL}/api/admin/packages`;
      
      const method = editingPackage?.id ? 'PUT' : 'POST';
      
      const response = await secureFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Eroare la salvare');
      }
      
      toast.success(editingPackage?.id ? 'Pachet actualizat cu succes!' : 'Pachet adăugat cu succes!');
      setShowForm(false);
      setEditingPackage(null);
      loadPackages();
    } catch (error) {
      console.error('Save package error:', error);
      toast.error(error.message || 'Eroare la salvarea pachetului');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeletePackage = async (packageId) => {
    
    try {
      const response = await secureFetch(`${API_URL}/api/admin/packages/${packageId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Delete failed');
      
      toast.success('Pachet șters cu succes');
      loadPackages();
    } catch (error) {
      console.error('Delete package error:', error);
      toast.error('Eroare la ștergerea pachetului');
    }
  };

  // Bulk selection functions
  const togglePackageSelection = (packageId) => {
    setSelectedPackages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(packageId)) {
        newSet.delete(packageId);
      } else {
        newSet.add(packageId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPackages.size === packages.length) {
      setSelectedPackages(new Set());
    } else {
      setSelectedPackages(new Set(packages.map(p => p.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPackages.size === 0) {
      toast.error('Selectează cel puțin un pachet');
      return;
    }

    if (!window.confirm(`Sigur vrei să ștergi ${selectedPackages.size} pachete? Această acțiune este ireversibilă!`)) {
      return;
    }

    setBulkDeleteLoading(true);

    try {
      const response = await secureFetch(`${API_URL}/api/admin/packages/bulk-delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ package_ids: Array.from(selectedPackages) })
      });

      if (!response.ok) throw new Error('Bulk delete failed');

      const result = await response.json();
      toast.success(`${result.deleted} pachete șterse cu succes!`);
      setSelectedPackages(new Set());
      loadPackages();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Eroare la ștergerea pachetelor');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleDeleteAllPackages = async () => {
    if (!window.confirm('Sigur vrei să ștergi TOATE pachetele? Această acțiune este ireversibilă!')) {
      return;
    }

    setBulkDeleteLoading(true);

    try {
      const response = await secureFetch(`${API_URL}/api/admin/packages/delete-all`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Delete all failed');

      const result = await response.json();
      toast.success(`${result.deleted} pachete șterse cu succes!`);
      setSelectedPackages(new Set());
      loadPackages();
    } catch (error) {
      console.error('Delete all error:', error);
      toast.error('Eroare la ștergerea pachetelor');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Inline price update for a single package
  const handleInlinePriceUpdate = async (packageId, newPrice, newOldPrice) => {
    try {
      const updateData = { price: newPrice };
      if (newOldPrice !== undefined) updateData.old_price = newOldPrice;
      
      const res = await secureFetch(`${API_URL}/api/admin/packages/${packageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });
      if (!res.ok) throw new Error('Update failed');
      
      // Update local state
      setPackages(prev => prev.map(p => p.id === packageId ? { ...p, price: newPrice, old_price: newOldPrice || p.old_price } : p));
      toast.success('Preț actualizat');
    } catch (e) {
      toast.error('Eroare la actualizare preț');
      throw e;
    }
  };

  // Bulk price change for selected packages
  const handleBulkPriceChange = async () => {
    if (selectedPackages.size === 0 || !bulkPriceValue) return;
    const val = parseFloat(bulkPriceValue);
    if (isNaN(val)) { toast.error('Introduceți o valoare numerică'); return; }
    
    setBulkPriceLoading(true);
    let updated = 0;
    let errors = 0;
    
    for (const pkgId of selectedPackages) {
      const pkg = packages.find(p => p.id === pkgId);
      if (!pkg) continue;
      
      // Extract numeric price
      const priceMatch = pkg.price.match(/([\d.,]+)/);
      if (!priceMatch) { errors++; continue; }
      const currentPrice = parseFloat(priceMatch[1].replace(',', '.'));
      const currency = pkg.price.replace(priceMatch[0], '').trim() || 'EUR';
      
      let newPrice;
      if (bulkPriceMode === 'percent') {
        newPrice = Math.round(currentPrice * (1 + val / 100));
      } else if (bulkPriceMode === 'fixed_add') {
        newPrice = Math.round(currentPrice + val);
      } else {
        newPrice = Math.round(val);
      }
      
      if (newPrice < 0) newPrice = 0;
      const newPriceStr = `${newPrice} ${currency}`;
      
      try {
        const res = await secureFetch(`${API_URL}/api/admin/packages/${pkgId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ price: newPriceStr })
        });
        if (res.ok) {
          setPackages(prev => prev.map(p => p.id === pkgId ? { ...p, price: newPriceStr } : p));
          updated++;
        } else { errors++; }
      } catch { errors++; }
    }
    
    setBulkPriceLoading(false);
    setShowBulkPrice(false);
    setBulkPriceValue('');
    setSelectedPackages(new Set());
    
    if (updated > 0) toast.success(`Preț actualizat pentru ${updated} pachete`);
    if (errors > 0) toast.error(`${errors} pachete nu au putut fi actualizate`);
  };

  const handleEditPackage = (pkg) => {
    setEditingPackage(pkg);
    if (pkg.category === 'Concerte') {
      setShowConcertFormAdmin(true);
      setShowForm(false);
    } else {
      setShowForm(true);
      setShowConcertFormAdmin(false);
    }
  };

  const handleDuplicatePackage = async (packageId) => {
    const confirmDuplicate = window.confirm(
      'Vrei să duplici acest pachet?\n\nNoua copie va include toate detaliile și poate fi editată înainte de publicare.'
    );
    
    if (!confirmDuplicate) return;
    
    setDuplicatingPackageId(packageId);
    
    try {
      const response = await secureFetch(`${API_URL}/api/admin/packages/${packageId}/duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Duplicare eșuată');
      }
      
      const result = await response.json();
      toast.success('Pachet duplicat cu succes! Poți edita copia acum.');
      
      // Open the edit form with the new duplicated package
      setEditingPackage(result.new_package);
      setShowForm(true);
      
      // Reload packages list to show the new package
      loadPackages();
      
    } catch (error) {
      console.error('Duplicate package error:', error);
      toast.error(error.message || 'Eroare la duplicarea pachetului');
    } finally {
      setDuplicatingPackageId(null);
    }
  };
  
  // Migration: Export packages to JSON file
  const handleExportPackages = async () => {
    setMigrationExporting(true);
    
    try {
      const operatorParam = selectedOperatorForExport !== 'all' 
        ? `?operator=${encodeURIComponent(selectedOperatorForExport)}` 
        : '';
      
      const response = await secureFetch(`${API_URL}/api/admin/export-packages${operatorParam}`);
      
      if (!response.ok) throw new Error('Export failed');
      
      const data = await response.json();
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const operatorName = selectedOperatorForExport !== 'all' ? selectedOperatorForExport.replace(/\s+/g, '_') : 'all';
      a.download = `vaai_packages_${operatorName}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Export complet: ${data.total} pachete exportate`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Eroare la export');
    } finally {
      setMigrationExporting(false);
    }
  };
  
  // Migration: Import packages from JSON file
  const handleImportPackages = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setMigrationImporting(true);
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.packages || !Array.isArray(data.packages)) {
        throw new Error('Format invalid - lipsește array-ul "packages"');
      }
      
      const response = await secureFetch(`${API_URL}/api/admin/import-packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) throw new Error('Import failed');
      
      const result = await response.json();
      toast.success(`Import complet: ${result.imported} noi, ${result.updated} actualizate, ${result.errors} erori`);
      
      // Reload packages
      loadPackages();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.message || 'Eroare la import');
    } finally {
      setMigrationImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };
  
  // Load geo data when tab changes
  useEffect(() => {
    if (activeTab === 'geo' && user) {
      loadGeoData();
    }
    if (activeTab === 'packages' && user) {
      // Load geo data for continent/country dropdowns in package form
      loadGeoData();
      loadOperators(); // Load operators for dropdown
    }
    if (activeTab === 'homepage' && user) {
      loadHomepageCards();
    }
    if (activeTab === 'operators' && user) {
      loadOperatorAccounts();
      loadPendingPackages();
    }
  }, [activeTab, user]);

  // ============ OPERATOR ACCOUNTS FUNCTIONS ============

  const loadOperatorAccounts = async () => {
    setOperatorsLoading(true);
    try {
      const res = await secureFetch(`${API_URL}/api/admin/operator-accounts`, {
      });
      if (res.ok) setOperatorAccounts(await res.json());
    } catch (e) { console.error('Load operators error:', e); }
    setOperatorsLoading(false);
  };

  const loadPendingPackages = async () => {
    try {
      const res = await secureFetch(`${API_URL}/api/admin/pending-packages`, {
      });
      if (res.ok) setPendingPackages(await res.json());
    } catch (e) { console.error('Load pending error:', e); }
  };

  const handleCreateOperator = async () => {
    if (!newOperator.email || !newOperator.password || !newOperator.name || !newOperator.operator_name) {
      toast.error('Completează toate câmpurile obligatorii');
      return;
    }
    try {
      const res = await secureFetch(`${API_URL}/api/admin/operator-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newOperator)
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Eroare la creare');
      }
      toast.success('Cont operator creat cu succes!');
      setShowAccountForm(false);
      setNewOperator({ email: '', password: '', name: '', operator_name: '', can_publish_directly: false });
      loadOperatorAccounts();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleToggleOperatorPublish = async (opId, currentValue) => {
    try {
      await secureFetch(`${API_URL}/api/admin/operator-accounts/${opId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ can_publish_directly: !currentValue })
      });
      toast.success('Setare actualizată');
      loadOperatorAccounts();
    } catch (e) { toast.error('Eroare la actualizare'); }
  };

  const handleToggleOperatorActive = async (opId, currentValue) => {
    try {
      await secureFetch(`${API_URL}/api/admin/operator-accounts/${opId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentValue })
      });
      toast.success(currentValue ? 'Cont dezactivat' : 'Cont activat');
      loadOperatorAccounts();
    } catch (e) { toast.error('Eroare la actualizare'); }
  };

  const handleApprovePackage = async (pkgId, action) => {
    try {
      const res = await secureFetch(`${API_URL}/api/admin/packages/${pkgId}/approve?action=${action}`, {
        method: 'PUT'
      });
      if (!res.ok) throw new Error('Eroare');
      toast.success(action === 'approve' ? 'Pachet aprobat!' : 'Pachet respins');
      loadPendingPackages();
    } catch (e) { toast.error('Eroare la procesare'); }
  };
  
  // ============ HOMEPAGE CARDS FUNCTIONS ============
  
  const loadHomepageCards = async () => {
    setHomepageCardsLoading(true);
    try {
      const response = await secureFetch(`${API_URL}/api/admin/homepage-cards`);
      if (response.ok) {
        setHomepageCards(await response.json());
      }
    } catch (error) {
      console.error('Load homepage cards error:', error);
      toast.error('Eroare la încărcarea cardurilor');
    } finally {
      setHomepageCardsLoading(false);
    }
  };

  const handleSaveHomepageCard = async (formData) => {
    try {
      const url = editingCard 
        ? `${API_URL}/api/admin/homepage-cards/${editingCard.id}`
        : `${API_URL}/api/admin/homepage-cards`;
      const method = editingCard ? 'PUT' : 'POST';
      
      const response = await secureFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Save failed');
      
      toast.success(editingCard ? 'Card actualizat!' : 'Card adăugat!');
      setShowCardForm(false);
      setEditingCard(null);
      loadHomepageCards();
    } catch (error) {
      console.error('Save homepage card error:', error);
      toast.error('Eroare la salvare');
    }
  };

  const handleDeleteHomepageCard = async (cardId) => {
    if (!confirm('Sigur vrei să ștergi acest card?')) return;
    
    try {
      const response = await secureFetch(`${API_URL}/api/admin/homepage-cards/${cardId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Delete failed');
      
      toast.success('Card șters!');
      loadHomepageCards();
    } catch (error) {
      console.error('Delete homepage card error:', error);
      toast.error('Eroare la ștergere');
    }
  };

  const handleToggleHomepageCard = async (cardId) => {
    try {
      const response = await secureFetch(`${API_URL}/api/admin/homepage-cards/${cardId}/toggle`, {
        method: 'PATCH'
      });
      
      if (!response.ok) throw new Error('Toggle failed');
      
      loadHomepageCards();
    } catch (error) {
      console.error('Toggle homepage card error:', error);
      toast.error('Eroare la schimbarea statusului');
    }
  };

  // ============ TOUR OPERATORS FUNCTIONS ============
  
  const loadOperators = async () => {
    setOperatorsLoading(true);
    try {
      // Use public endpoint for dropdown (doesn't require auth)
      const response = await fetch(`${API_URL}/api/operators`);
      if (response.ok) {
        const data = await response.json();
        setOperators(data);
      }
    } catch (err) {
      console.error('[Dashboard] Failed to load operators:', err);
    } finally {
      setOperatorsLoading(false);
    }
  };

  const handleSaveOperator = async () => {
    if (!newOperatorName.trim()) {
      toast.error('Numele operatorului este obligatoriu');
      return;
    }
    
    try {
      const url = editingOperator 
        ? `${API_URL}/api/admin/operators/${editingOperator.id}`
        : `${API_URL}/api/admin/operators`;
      const method = editingOperator ? 'PUT' : 'POST';
      
      const response = await secureFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newOperatorName.trim(), is_active: true, display_order: operators.length })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Save failed');
      }
      
      toast.success(editingOperator ? 'Operator actualizat!' : 'Operator adăugat!');
      setShowOperatorForm(false);
      setEditingOperator(null);
      setNewOperatorName('');
      loadOperators();
    } catch (error) {
      console.error('Save operator error:', error);
      toast.error(error.message || 'Eroare la salvare');
    }
  };

  const handleDeleteOperator = async (operatorId) => {
    if (!confirm('Sigur vrei să ștergi acest operator?')) return;
    
    try {
      const response = await secureFetch(`${API_URL}/api/admin/operators/${operatorId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Delete failed');
      
      toast.success('Operator șters!');
      loadOperators();
    } catch (error) {
      console.error('Delete operator error:', error);
      toast.error('Eroare la ștergere');
    }
  };

  const handleToggleOperator = async (operatorId) => {
    try {
      const response = await secureFetch(`${API_URL}/api/admin/operators/${operatorId}/toggle`, {
        method: 'PATCH'
      });
      
      if (!response.ok) throw new Error('Toggle failed');
      
      loadOperators();
    } catch (error) {
      console.error('Toggle operator error:', error);
      toast.error('Eroare la schimbarea statusului');
    }
  };

  // ============ PACKAGE SEARCH FOR HOMEPAGE CARDS ============
  
  const searchPackagesForCard = async (query) => {
    if (!query || query.length < 2) {
      setPackageSearchResults([]);
      return;
    }
    
    setPackageSearchLoading(true);
    try {
      const response = await secureFetch(`${API_URL}/api/admin/packages/search?q=${encodeURIComponent(query)}&limit=20`);
      if (response.ok) {
        setPackageSearchResults(await response.json());
      }
    } catch (error) {
      console.error('Package search error:', error);
    } finally {
      setPackageSearchLoading(false);
    }
  };

  const togglePackageInCard = (packageId) => {
    setCardFormData(prev => {
      const ids = prev.selected_package_ids || [];
      if (ids.includes(packageId)) {
        return { ...prev, selected_package_ids: ids.filter(id => id !== packageId) };
      } else {
        return { ...prev, selected_package_ids: [...ids, packageId] };
      }
    });
  };

  const resetCardForm = () => {
    setCardFormData({
      title: '', slug: '', description: '', image_url: '', link_url: '', link_text: 'Vezi mai mult',
      display_order: 0, is_active: true, card_type: 'link', selected_package_ids: []
    });
    setPackageSearchQuery('');
    setPackageSearchResults([]);
  };

  const openCardFormForEdit = (card) => {
    setEditingCard(card);
    setCardFormData({
      title: card.title || '',
      slug: card.slug || '',
      description: card.description || '',
      image_url: card.image_url || '',
      link_url: card.link_url || '',
      link_text: card.link_text || 'Vezi mai mult',
      display_order: card.display_order || 0,
      is_active: card.is_active !== false,
      card_type: card.card_type || 'link',
      selected_package_ids: card.selected_package_ids || []
    });
    setShowCardForm(true);
  };

  // ============ GEO (CONTINENTS/COUNTRIES) FUNCTIONS ============
  
  const loadGeoData = async () => {
    setGeoLoading(true);
    try {
      const [continentsRes, countriesRes] = await Promise.all([
        secureFetch(`${API_URL}/api/admin/continents`),
        secureFetch(`${API_URL}/api/admin/countries`)
      ]);
      
      if (continentsRes.ok) setContinents(await continentsRes.json());
      if (countriesRes.ok) setCountries(await countriesRes.json());
    } catch (error) {
      console.error('Load geo error:', error);
      toast.error('Eroare la încărcarea datelor geografice');
    } finally {
      setGeoLoading(false);
    }
  };
  
  const handleSaveContinent = async (formData) => {
    try {
      const url = editingContinent 
        ? `${API_URL}/api/admin/continents/${editingContinent.id}`
        : `${API_URL}/api/admin/continents`;
      const method = editingContinent ? 'PUT' : 'POST';
      
      const response = await secureFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Eroare');
      }
      
      toast.success(editingContinent ? 'Continent actualizat' : 'Continent creat');
      setShowContinentForm(false);
      setEditingContinent(null);
      loadGeoData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleDeleteContinent = async (id) => {
    if (!confirm('Sigur vrei să ștergi acest continent?')) return;
    try {
      const response = await secureFetch(`${API_URL}/api/admin/continents/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Eroare');
      }
      toast.success('Continent șters');
      loadGeoData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleToggleContinent = async (id) => {
    try {
      const response = await secureFetch(`${API_URL}/api/admin/continents/${id}/toggle`, {
        method: 'PATCH'
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        loadGeoData();
      }
    } catch (error) {
      toast.error('Eroare la schimbarea statusului');
    }
  };
  
  const handleSaveCountry = async (formData) => {
    try {
      const url = editingCountry 
        ? `${API_URL}/api/admin/countries/${editingCountry.id}`
        : `${API_URL}/api/admin/countries`;
      const method = editingCountry ? 'PUT' : 'POST';
      
      const response = await secureFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Eroare');
      }
      
      toast.success(editingCountry ? 'Țară actualizată' : 'Țară creată');
      setShowCountryForm(false);
      setEditingCountry(null);
      loadGeoData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleDeleteCountry = async (id) => {
    if (!confirm('Sigur vrei să ștergi această țară?')) return;
    try {
      const response = await secureFetch(`${API_URL}/api/admin/countries/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Eroare la ștergere');
      toast.success('Țară ștearsă');
      loadGeoData();
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const handleToggleCountry = async (id) => {
    try {
      const response = await secureFetch(`${API_URL}/api/admin/countries/${id}/toggle`, {
        method: 'PATCH'
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        loadGeoData();
      }
    } catch (error) {
      toast.error('Eroare la schimbarea statusului');
    }
  };
  
  const filteredCountries = selectedContinentFilter === 'all' 
    ? countries 
    : countries.filter(c => c.continent_id === selectedContinentFilter);

  const formatRelativeTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `acum ${minutes} minute`;
    if (hours < 24) return `acum ${hours} ore`;
    if (days === 1) return 'ieri';
    if (days < 7) return `acum ${days} zile`;
    return date.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading || !stats) {
    return (
      <div className="min-h-screen pt-24 bg-[#F8F9FA] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-16 bg-[#F8F9FA]" data-testid="dashboard-page">
      {/* Operator Add/Edit Modal */}
      {showOperatorForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h3 className="font-heading font-semibold text-lg text-navy mb-4">
              {editingOperator ? 'Editează Operator' : 'Adaugă Operator Nou'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-navy mb-1">Nume operator</label>
                <input
                  type="text"
                  value={newOperatorName}
                  onChange={(e) => setNewOperatorName(e.target.value)}
                  placeholder="Ex: TUI, Paralela 45..."
                  className="w-full px-4 py-2.5 rounded-xl border border-navy/20 bg-white text-navy focus:border-gold focus:ring-1 focus:ring-gold/50"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveOperator} className="flex-1 bg-gold text-navy hover:bg-gold-hover">
                  <Save className="w-4 h-4 mr-2" />
                  Salvează
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => { setShowOperatorForm(false); setEditingOperator(null); setNewOperatorName(''); }}
                  className="border-navy/20"
                >
                  <X className="w-4 h-4 mr-2" />
                  Anulează
                </Button>
              </div>
            </div>
            
            {/* Existing operators list */}
            <div className="mt-6 border-t border-navy/10 pt-4">
              <h4 className="text-sm font-medium text-navy/70 mb-3">Operatori existenți ({operators.length})</h4>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {operators.map(op => (
                  <div key={op.id} className="flex items-center justify-between p-2 bg-navy/5 rounded-lg">
                    <span className={`text-sm ${op.is_active ? 'text-navy' : 'text-navy/50 line-through'}`}>
                      {op.name}
                    </span>
                    <div className="flex gap-1">
                      <button 
                        onClick={() => handleToggleOperator(op.id)}
                        className={`p-1 rounded ${op.is_active ? 'text-green-600 hover:bg-green-50' : 'text-red-600 hover:bg-red-50'}`}
                        title={op.is_active ? 'Dezactivează' : 'Activează'}
                      >
                        {op.is_active ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </button>
                      <button 
                        onClick={() => handleDeleteOperator(op.id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                        title="Șterge"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 pt-4">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-navy" data-testid="dashboard-title">
              Dashboard VaAi – Administrare
            </h1>
            <p className="text-navy/60 mt-1">
              Bine ai venit, {user?.name}! Monitorizează și gestionează platforma.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all ${
              activeTab === 'analytics'
                ? 'bg-gold text-navy'
                : 'bg-white border border-navy/10 text-navy/70 hover:bg-navy/5'
            }`}
            data-testid="tab-analytics"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Analiză & Metrici
          </Button>
          <Button
            onClick={() => setActiveTab('packages')}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all ${
              activeTab === 'packages'
                ? 'bg-gold text-navy'
                : 'bg-white border border-navy/10 text-navy/70 hover:bg-navy/5'
            }`}
            data-testid="tab-packages"
          >
            <Package className="w-4 h-4 mr-2" />
            Gestionare Pachete
          </Button>
          <Button
            onClick={() => setActiveTab('geo')}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all ${
              activeTab === 'geo'
                ? 'bg-gold text-navy'
                : 'bg-white border border-navy/10 text-navy/70 hover:bg-navy/5'
            }`}
            data-testid="tab-geo"
          >
            <MapPin className="w-4 h-4 mr-2" />
            Continente & Țări
          </Button>
          <Button
            onClick={() => setActiveTab('homepage')}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all ${
              activeTab === 'homepage'
                ? 'bg-gold text-navy'
                : 'bg-white border border-navy/10 text-navy/70 hover:bg-navy/5'
            }`}
            data-testid="tab-homepage"
          >
            <Layers className="w-4 h-4 mr-2" />
            Carduri Homepage
          </Button>
          <Button
            onClick={() => setActiveTab('operators')}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all ${
              activeTab === 'operators'
                ? 'bg-gold text-navy'
                : 'bg-white border border-navy/10 text-navy/70 hover:bg-navy/5'
            }`}
            data-testid="tab-operators"
          >
            <Users className="w-4 h-4 mr-2" />
            Operatori
          </Button>
          <Button
            onClick={() => setActiveTab('event-categories')}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all ${
              activeTab === 'event-categories'
                ? 'bg-gold text-navy'
                : 'bg-white border border-navy/10 text-navy/70 hover:bg-navy/5'
            }`}
            data-testid="tab-event-categories"
          >
            <Tag className="w-4 h-4 mr-2" />
            Categorii Evenimente
          </Button>
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <>
            {/* Filters */}
            <div className="flex items-center gap-3 mb-8">
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-40 bg-white border-navy/20" data-testid="date-range-select">
                  <SelectValue placeholder="Perioada" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Ultimele 7 zile</SelectItem>
                  <SelectItem value="30">Ultimele 30 zile</SelectItem>
                  <SelectItem value="90">Ultimele 90 zile</SelectItem>
                  <SelectItem value="365">Ultimul an</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                onClick={handleReset}
                variant="outline"
                className="border-navy/20 text-navy hover:bg-navy/5"
                data-testid="reset-filters-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              
              <Button
                onClick={handleExport}
                className="bg-gold hover:bg-gold-hover text-navy font-semibold"
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {/* Total Clicks */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-navy/10 shadow-sm" data-testid="stat-total-clicks">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <MousePointer2 className="w-6 h-6 text-gold" />
                  </div>
                </div>
                <p className="text-navy/60 text-sm mb-1">Total Click-uri „Rezervă"</p>
                <p className="text-gold font-bold text-3xl">{stats.total_clicks.toLocaleString()}</p>
              </div>

              {/* Clicks 7 Days */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-navy/10 shadow-sm" data-testid="stat-7days-clicks">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-turquoise/10 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-turquoise" />
                  </div>
                  <span className={`text-sm font-semibold px-2 py-1 rounded-full ${
                    stats.percent_change >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {stats.percent_change >= 0 ? '+' : ''}{stats.percent_change}%
                  </span>
                </div>
                <p className="text-navy/60 text-sm mb-1">Ultimele 7 zile</p>
                <p className="text-navy font-bold text-3xl">{stats.clicks_7days.toLocaleString()}</p>
              </div>

              {/* Estimated Commission */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-navy/10 shadow-sm" data-testid="stat-commission">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-500" />
                  </div>
                </div>
                <p className="text-navy/60 text-sm mb-1">Estimare Comisioane</p>
                <p className="text-gold font-bold text-3xl">{stats.estimated_commission.toLocaleString()} €</p>
                <p className="text-navy/40 text-xs mt-1">5% × 50€ medie/pachet</p>
              </div>

              {/* Conversion Rate */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-navy/10 shadow-sm" data-testid="stat-conversion">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-purple-500" />
                  </div>
                </div>
                <p className="text-navy/60 text-sm mb-1">Clicks/Zi (medie)</p>
                <p className="text-navy font-bold text-3xl">
                  {(stats.total_clicks / parseInt(dateRange) || 0).toFixed(1)}
                </p>
              </div>
            </div>

            {/* Additional Stats Row */}
            {stats.total_packages !== undefined && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {/* Total Packages */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-navy/10 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                      <Package className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-navy/60 text-xs">Total Pachete Active</p>
                      <p className="text-navy font-bold text-xl">{stats.total_packages?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Operators Tracked */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-navy/10 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-indigo-500" />
                    </div>
                    <div>
                      <p className="text-navy/60 text-xs">Operatori cu Click-uri</p>
                      <p className="text-navy font-bold text-xl">{stats.total_operators_tracked || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Top Package */}
                {stats.top_packages && stats.top_packages.length > 0 && (
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-navy/10 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-orange-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-navy/60 text-xs">Cel mai popular pachet</p>
                        <p className="text-navy font-bold text-sm truncate" title={stats.top_packages[0].name}>
                          {stats.top_packages[0].name?.substring(0, 30)}...
                        </p>
                        <p className="text-navy/50 text-xs">{stats.top_packages[0].count} click-uri</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Bar Chart - Top Operators */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-navy/10 shadow-sm" data-testid="chart-operators">
                <h3 className="font-heading font-semibold text-lg text-navy mb-4">
                  Top 8 Operatori după Click-uri
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.clicks_by_operator} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" tick={{ fill: '#0A2540', fontSize: 12 }} />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={100} 
                        tick={{ fill: '#0A2540', fontSize: 11 }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#0A2540', 
                          border: 'none', 
                          borderRadius: '8px',
                          color: '#fff' 
                        }}
                      />
                      <Bar dataKey="count" fill="#D4AF37" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Pie Chart - Categories */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-navy/10 shadow-sm" data-testid="chart-categories">
                <h3 className="font-heading font-semibold text-lg text-navy mb-4">
                  Distribuție Click-uri pe Categorii
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.clicks_by_category.filter(c => c.name)}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="count"
                        nameKey="name"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {stats.clicks_by_category.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#0A2540', 
                          border: 'none', 
                          borderRadius: '8px',
                          color: '#fff' 
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Line Chart - Daily Trend */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-navy/10 shadow-sm mb-8" data-testid="chart-trend">
              <h3 className="font-heading font-semibold text-lg text-navy mb-4">
                Evoluție Click-uri (ultimele 30 zile)
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.daily_clicks}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fill: '#0A2540', fontSize: 10 }}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' })}
                    />
                    <YAxis tick={{ fill: '#0A2540', fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0A2540', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: '#fff' 
                      }}
                      labelFormatter={(value) => new Date(value).toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' })}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="clicks" 
                      stroke="#D4AF37" 
                      strokeWidth={3}
                      dot={{ fill: '#D4AF37', r: 4 }}
                      activeDot={{ r: 6, fill: '#40C4FF' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent Clicks Table */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-navy/10 shadow-sm" data-testid="recent-clicks-table">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold text-lg text-navy">
                  Click-uri Recente (ultimele 100)
                </h3>
                <span className="text-navy/40 text-sm">{recentClicks.length} înregistrări</span>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-navy/10">
                      <th className="text-left py-3 px-4 text-navy/60 text-sm font-medium">Timestamp</th>
                      <th className="text-left py-3 px-4 text-navy/60 text-sm font-medium">Operator</th>
                      <th className="text-left py-3 px-4 text-navy/60 text-sm font-medium">Pachet</th>
                      <th className="text-left py-3 px-4 text-navy/60 text-sm font-medium">Sursă</th>
                      <th className="text-left py-3 px-4 text-navy/60 text-sm font-medium">User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentClicks.map((click, index) => (
                      <tr 
                        key={click.id || index} 
                        className="border-b border-navy/5 hover:bg-navy/5 transition-colors"
                      >
                        <td className="py-3 px-4 text-navy text-sm">
                          {formatRelativeTime(click.timestamp)}
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-navy font-medium text-sm">{click.operator}</span>
                        </td>
                        <td className="py-3 px-4">
                          <a 
                            href={`/pachet/${click.package_id}`}
                            className="text-gold hover:underline text-sm flex items-center gap-1"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {click.package_title?.substring(0, 40)}...
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 bg-navy/5 rounded text-navy/70 text-xs">
                            {click.source_page || 'homepage'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-navy/60 text-sm">
                          {click.user_email || 'Anonim'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Detailed Package Analytics */}
            <div className="mt-8">
              <h3 className="text-xl font-bold text-navy mb-4">Performanță pachete</h3>
              <AnalyticsPanel />
            </div>
          </>
        )}

        {/* Packages Tab */}
        {activeTab === 'packages' && (
          <>
            {/* Add Package Section */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-navy/10 shadow-sm mb-8 overflow-hidden">
              <div 
                className="p-6 border-b border-navy/10 flex items-center justify-between cursor-pointer hover:bg-navy/5 transition-colors"
                onClick={() => {
                  if (!showForm) {
                    setEditingPackage(null);
                  }
                  setShowForm(!showForm);
                }}
                data-testid="add-package-header"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <Plus className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-navy">
                      {editingPackage ? 'Editează Pachet' : 'Adaugă Program Nou'}
                    </h3>
                    <p className="text-navy/60 text-sm">
                      {editingPackage ? `Editezi: ${editingPackage.title}` : 'Adaugă manual pachete de la touroperatori'}
                    </p>
                  </div>
                </div>
                {showForm ? (
                  <ChevronUp className="w-5 h-5 text-navy/40" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-navy/40" />
                )}
              </div>
              
              {showForm && (
                <div className="p-6" data-testid="package-form-container">
                  <PackageForm
                    key={editingPackage?.id || 'new'}
                    initialData={editingPackage || EMPTY_PACKAGE}
                    onSave={handleSavePackage}
                    onCancel={() => {
                      setShowForm(false);
                      setEditingPackage(null);
                    }}
                    loading={formLoading}
                    continents={continents}
                    countries={countries}
                    operators={operators}
                    onAddOperator={() => { setShowOperatorForm(true); setNewOperatorName(''); setEditingOperator(null); }}
                  />
                </div>
              )}
            </div>

            {/* Add Concert Section */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-purple-200 shadow-sm mb-8 overflow-hidden">
              <div 
                className="p-6 border-b border-purple-100 flex items-center justify-between cursor-pointer hover:bg-purple-50/50 transition-colors"
                onClick={() => {
                  if (!showConcertFormAdmin) setEditingPackage(null);
                  setShowConcertFormAdmin(!showConcertFormAdmin);
                  if (!showConcertFormAdmin) setShowForm(false);
                }}
                data-testid="add-concert-header"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Music className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-navy">
                      {editingPackage?.category === 'Concerte' ? 'Editează Concert / Eveniment' : 'Adaugă Concert / Eveniment'}
                    </h3>
                    <p className="text-navy/60 text-sm">
                      {editingPackage?.category === 'Concerte' ? `Editezi: ${editingPackage.title}` : 'Adaugă concerte muzicale sau evenimente (Cultural / Sport / Altele)'}
                    </p>
                  </div>
                </div>
                {showConcertFormAdmin ? <ChevronUp className="w-5 h-5 text-navy/40" /> : <ChevronDown className="w-5 h-5 text-navy/40" />}
              </div>
              {showConcertFormAdmin && (
                <div className="p-6">
                  <ConcertForm
                    initialData={editingPackage?.category === 'Concerte' ? editingPackage : null}
                    onSave={async (data) => {
                      await handleSavePackage(data);
                      setShowConcertFormAdmin(false);
                    }}
                    onCancel={() => { setShowConcertFormAdmin(false); setEditingPackage(null); }}
                    loading={loading}
                  />
                </div>
              )}
            </div>

            {/* Existing Packages Table */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-navy/10 shadow-sm" data-testid="packages-table">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div>
                  <h3 className="font-heading font-semibold text-lg text-navy">
                    Programe Existente
                  </h3>
                  <p className="text-navy/50 text-sm">
                    {searchTerm 
                      ? `${packagesTotal} rezultate pentru "${searchTerm}"` 
                      : `${packagesTotal} pachete în total`
                    }
                    {selectedPackages.size > 0 && (
                      <span className="ml-2 text-red-600 font-medium">
                        ({selectedPackages.size} selectate)
                      </span>
                    )}
                  </p>
                </div>
                
                {/* Search Bar */}
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      placeholder="Caută după nume..."
                      className="pl-10 pr-4 py-2 w-64 rounded-xl border bg-white text-navy border-navy/20 focus:border-gold focus:ring-1 focus:ring-gold/50 text-sm"
                      data-testid="package-search-input"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => handleSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <Button
                    onClick={() => loadPackages(currentPage, searchTerm)}
                    variant="outline"
                    className="border-navy/20 text-navy hover:bg-navy/5"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${packagesLoading ? 'animate-spin' : ''}`} />
                    Reîncarcă
                  </Button>
                </div>
              </div>

              {/* Bulk Actions Bar */}
              {packages.length > 0 && (
                <div className="flex items-center gap-3 mb-4 p-3 bg-navy/5 rounded-xl">
                  <Button
                    onClick={toggleSelectAll}
                    variant="outline"
                    size="sm"
                    className="border-navy/20 text-navy hover:bg-navy/10"
                  >
                    {selectedPackages.size === packages.length ? 'Deselectează tot' : 'Selectează tot'}
                  </Button>
                  
                  {selectedPackages.size > 0 && (
                    <>
                      <Button
                        onClick={() => setShowBulkPrice(!showBulkPrice)}
                        size="sm"
                        className="bg-gold hover:bg-gold/90 text-navy"
                        data-testid="bulk-price-btn"
                      >
                        <DollarSign className="w-4 h-4 mr-2" />
                        Modifică Prețuri ({selectedPackages.size})
                      </Button>
                      <Button
                        onClick={handleBulkDelete}
                        disabled={bulkDeleteLoading}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        {bulkDeleteLoading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        ) : (
                          <Trash2 className="w-4 h-4 mr-2" />
                        )}
                        Șterge selectate ({selectedPackages.size})
                      </Button>
                    </>
                  )}
                  
                  <div className="ml-auto">
                    <Button
                      onClick={handleDeleteAllPackages}
                      disabled={bulkDeleteLoading || packages.length === 0}
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Șterge TOATE ({packagesTotal})
                    </Button>
                  </div>
                </div>
              )}
              
              {packagesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
              ) : packages.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-navy/20 mx-auto mb-3" />
                  <p className="text-navy/60">Nu există pachete încă</p>
                  <Button
                    onClick={() => setShowForm(true)}
                    className="mt-4 bg-gold hover:bg-gold-hover text-navy"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adaugă primul pachet
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {/* Bulk Price Modification Panel */}
                  {showBulkPrice && selectedPackages.size > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-testid="bulk-price-panel">
                      <h4 className="font-bold text-navy text-sm mb-3">Modificare preț in masă ({selectedPackages.size} pachete selectate)</h4>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="block text-xs text-navy/60 mb-1">Tip modificare</label>
                          <select value={bulkPriceMode} onChange={e => setBulkPriceMode(e.target.value)}
                            className="px-3 py-2 border border-navy/20 rounded-lg text-sm text-navy" data-testid="bulk-price-mode">
                            <option value="percent">Procentual (%)</option>
                            <option value="fixed_add">Adaugă/Scade sumă fixă</option>
                            <option value="fixed_set">Setează preț fix</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-navy/60 mb-1">
                            {bulkPriceMode === 'percent' ? 'Procent (ex: 10 sau -15)' : bulkPriceMode === 'fixed_add' ? 'Sumă (ex: 50 sau -30)' : 'Preț nou'}
                          </label>
                          <input type="number" value={bulkPriceValue} onChange={e => setBulkPriceValue(e.target.value)}
                            className="px-3 py-2 border border-navy/20 rounded-lg text-sm text-navy w-32"
                            placeholder={bulkPriceMode === 'percent' ? '10' : '100'} data-testid="bulk-price-value" />
                        </div>
                        <Button onClick={handleBulkPriceChange} disabled={bulkPriceLoading || !bulkPriceValue}
                          className="bg-gold text-navy hover:bg-gold/90" data-testid="bulk-price-apply">
                          {bulkPriceLoading ? <div className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin mr-2" /> : null}
                          Aplică
                        </Button>
                        <Button onClick={() => { setShowBulkPrice(false); setBulkPriceValue(''); }} variant="ghost" className="text-navy/60">
                          Anulează
                        </Button>
                      </div>
                      <p className="text-xs text-navy/40 mt-2">
                        {bulkPriceMode === 'percent' && bulkPriceValue ? `Exemplu: 1000 EUR → ${Math.round(1000 * (1 + parseFloat(bulkPriceValue) / 100))} EUR. Prețul vechi salvat automat.` : ''}
                        {bulkPriceMode === 'fixed_add' && bulkPriceValue ? `Exemplu: 1000 EUR → ${1000 + parseFloat(bulkPriceValue)} EUR. Prețul vechi salvat automat.` : ''}
                        {bulkPriceMode === 'fixed_set' && bulkPriceValue ? `Toate pachetele selectate vor avea prețul: ${bulkPriceValue} EUR` : ''}
                      </p>
                    </div>
                  )}

                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-navy/10">
                        <th className="text-left py-3 px-2 text-navy/60 text-sm font-medium w-10">
                          <input
                            type="checkbox"
                            checked={selectedPackages.size === packages.length && packages.length > 0}
                            onChange={toggleSelectAll}
                            className="w-4 h-4 rounded border-navy/30 text-gold focus:ring-gold"
                          />
                        </th>
                        <th className="text-left py-3 px-4 text-navy/60 text-sm font-medium">Pachet</th>
                        <th className="text-left py-3 px-4 text-navy/60 text-sm font-medium">Operator</th>
                        <th className="text-left py-3 px-4 text-navy/60 text-sm font-medium">Preț</th>
                        <th className="text-left py-3 px-4 text-navy/60 text-sm font-medium">Categorie/Locație</th>
                        <th className="text-left py-3 px-4 text-navy/60 text-sm font-medium">Durată</th>
                        <th className="text-center py-3 px-4 text-navy/60 text-sm font-medium">Click-uri</th>
                        <th className="text-left py-3 px-4 text-navy/60 text-sm font-medium">Acțiuni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packages.map((pkg) => (
                        <PackageRow
                          key={pkg.id}
                          pkg={pkg}
                          onEdit={handleEditPackage}
                          onDelete={handleDeletePackage}
                          onDuplicate={handleDuplicatePackage}
                          isDuplicating={duplicatingPackageId === pkg.id}
                          isSelected={selectedPackages.has(pkg.id)}
                          onToggleSelect={() => togglePackageSelection(pkg.id)}
                          onPriceUpdate={handleInlinePriceUpdate}
                        />
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-navy/10">
                      <p className="text-navy/60 text-sm">
                        Pagina {currentPage} din {totalPages} ({packagesTotal} pachete total)
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => handlePageChange(1)}
                          disabled={currentPage === 1}
                          variant="outline"
                          size="sm"
                          className="border-navy/20 text-navy hover:bg-navy/5 disabled:opacity-50"
                        >
                          ««
                        </Button>
                        <Button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          variant="outline"
                          size="sm"
                          className="border-navy/20 text-navy hover:bg-navy/5 disabled:opacity-50"
                        >
                          ‹ Anterior
                        </Button>
                        
                        {/* Page numbers */}
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                variant={currentPage === pageNum ? "default" : "outline"}
                                size="sm"
                                className={currentPage === pageNum 
                                  ? "bg-gold text-navy hover:bg-gold-hover" 
                                  : "border-navy/20 text-navy hover:bg-navy/5"
                                }
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>
                        
                        <Button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          variant="outline"
                          size="sm"
                          className="border-navy/20 text-navy hover:bg-navy/5 disabled:opacity-50"
                        >
                          Următor ›
                        </Button>
                        <Button
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                          variant="outline"
                          size="sm"
                          className="border-navy/20 text-navy hover:bg-navy/5 disabled:opacity-50"
                        >
                          »»
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Geo (Continents & Countries) Tab */}
        {activeTab === 'geo' && (
          <div className="space-y-6">
            {/* Continents Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-navy flex items-center gap-2">
                    <Globe className="w-6 h-6 text-gold" />
                    Continente
                  </h2>
                  <p className="text-navy/60 mt-1">Gestionează continentele disponibile pe platformă</p>
                </div>
                <Button
                  onClick={() => { setEditingContinent(null); setShowContinentForm(true); }}
                  className="bg-gold hover:bg-gold-hover text-navy"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adaugă Continent
                </Button>
              </div>

              {/* Continent Form Modal */}
              {showContinentForm && (
                <div className="mb-4 p-4 bg-navy/5 rounded-xl">
                  <h3 className="font-semibold text-navy mb-3">
                    {editingContinent ? 'Editează Continent' : 'Adaugă Continent Nou'}
                  </h3>
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    handleSaveContinent({
                      name: formData.get('name'),
                      description: formData.get('description'),
                      image_url: formData.get('image_url'),
                      display_order: parseInt(formData.get('display_order')) || 0,
                      is_active: formData.get('is_active') === 'on'
                    });
                  }} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <input name="name" defaultValue={editingContinent?.name || ''} placeholder="Nume continent" required className="px-3 py-2 border rounded-lg bg-white text-navy" />
                      <input name="display_order" type="number" defaultValue={editingContinent?.display_order || 0} placeholder="Ordine afișare" className="px-3 py-2 border rounded-lg bg-white text-navy" />
                    </div>
                    <input name="description" defaultValue={editingContinent?.description || ''} placeholder="Descriere" className="w-full px-3 py-2 border rounded-lg bg-white text-navy" />
                    <input name="image_url" defaultValue={editingContinent?.image_url || ''} placeholder="URL imagine" className="w-full px-3 py-2 border rounded-lg bg-white text-navy" />
                    <div className="flex items-center gap-2">
                      <input type="checkbox" name="is_active" id="continent_active" defaultChecked={editingContinent?.is_active !== false} className="w-4 h-4" />
                      <label htmlFor="continent_active" className="text-sm text-navy/70">Activ</label>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="bg-gold text-navy"><Save className="w-4 h-4 mr-1" />Salvează</Button>
                      <Button type="button" variant="outline" onClick={() => { setShowContinentForm(false); setEditingContinent(null); }}><X className="w-4 h-4 mr-1" />Anulează</Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Continents Table */}
              {geoLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-navy/60 border-b border-navy/10">
                        <th className="pb-3 font-medium">Nume</th>
                        <th className="pb-3 font-medium">Descriere</th>
                        <th className="pb-3 font-medium">Ordine</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium text-right">Acțiuni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {continents.map(cont => (
                        <tr key={cont.id} className="border-b border-navy/5 last:border-0">
                          <td className="py-3 font-medium text-navy">{cont.name}</td>
                          <td className="py-3 text-navy/70 text-sm">{cont.description || '-'}</td>
                          <td className="py-3 text-navy/70">{cont.display_order}</td>
                          <td className="py-3">
                            <button onClick={() => handleToggleContinent(cont.id)} className={`px-2 py-1 rounded-full text-xs font-medium ${cont.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {cont.is_active ? 'Activ' : 'Inactiv'}
                            </button>
                          </td>
                          <td className="py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setEditingContinent(cont); setShowContinentForm(true); }}><Edit2 className="w-3 h-3" /></Button>
                              <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteContinent(cont.id)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Countries Section */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-navy/5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-navy flex items-center gap-2">
                    <MapPin className="w-6 h-6 text-gold" />
                    Țări ({filteredCountries.length})
                  </h2>
                  <p className="text-navy/60 mt-1">Gestionează țările din fiecare continent</p>
                </div>
                <div className="flex gap-2">
                  <Select value={selectedContinentFilter} onValueChange={setSelectedContinentFilter}>
                    <SelectTrigger className="w-48 bg-white">
                      <SelectValue placeholder="Filtrează după continent" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate continentele</SelectItem>
                      {continents.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => { setEditingCountry(null); setShowCountryForm(true); }}
                    className="bg-gold hover:bg-gold-hover text-navy"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adaugă Țară
                  </Button>
                </div>
              </div>

              {/* Country Form Modal */}
              {showCountryForm && (
                <div className="mb-4 p-4 bg-navy/5 rounded-xl">
                  <h3 className="font-semibold text-navy mb-3">
                    {editingCountry ? 'Editează Țară' : 'Adaugă Țară Nouă'}
                  </h3>
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const selectedContinent = continents.find(c => c.id === formData.get('continent_id'));
                    // Get all checked categories
                    const selectedCategories = formData.getAll('categories');
                    handleSaveCountry({
                      name: formData.get('name'),
                      continent_id: formData.get('continent_id'),
                      continent_name: selectedContinent?.name || '',
                      categories: selectedCategories.length > 0 ? selectedCategories : ['Circuite Externe'],
                      description: formData.get('description'),
                      image_url: formData.get('image_url'),
                      display_order: parseInt(formData.get('display_order')) || 0,
                      is_active: formData.get('is_active') === 'on'
                    });
                  }} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <input name="name" defaultValue={editingCountry?.name || ''} placeholder="Nume țară" required className="px-3 py-2 border rounded-lg bg-white text-navy" data-testid="country-name-input" />
                      <select name="continent_id" defaultValue={editingCountry?.continent_id || ''} required className="px-3 py-2 border rounded-lg bg-white text-navy" data-testid="country-continent-select">
                        <option value="">Selectează continent</option>
                        {continents.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-navy/70">Categorii (selectează multiple)</label>
                        <div className="flex flex-wrap gap-3 p-3 border rounded-lg bg-white" data-testid="country-categories-checkboxes">
                          {['Circuite Externe', 'City Break', 'Circuite Romania', 'Concerte'].map(cat => {
                            // Support both old 'category' field and new 'categories' array
                            const existingCategories = editingCountry?.categories || (editingCountry?.category ? [editingCountry.category] : ['Circuite Externe']);
                            return (
                              <label key={cat} className="flex items-center gap-2 cursor-pointer">
                                <input 
                                  type="checkbox" 
                                  name="categories" 
                                  value={cat}
                                  defaultChecked={existingCategories.includes(cat)}
                                  className="w-4 h-4 rounded border-navy/30 text-gold focus:ring-gold"
                                  data-testid={`category-checkbox-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                                />
                                <span className="text-sm text-navy">{cat}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <input name="display_order" type="number" defaultValue={editingCountry?.display_order || 0} placeholder="Ordine afișare" className="px-3 py-2 border rounded-lg bg-white text-navy h-fit" data-testid="country-display-order-input" />
                    </div>
                    <input name="description" defaultValue={editingCountry?.description || ''} placeholder="Descriere" className="w-full px-3 py-2 border rounded-lg bg-white text-navy" data-testid="country-description-input" />
                    <input name="image_url" defaultValue={editingCountry?.image_url || ''} placeholder="URL imagine" className="w-full px-3 py-2 border rounded-lg bg-white text-navy" data-testid="country-image-url-input" />
                    <div className="flex items-center gap-2">
                      <input type="checkbox" name="is_active" id="country_active" defaultChecked={editingCountry?.is_active !== false} className="w-4 h-4" data-testid="country-is-active-checkbox" />
                      <label htmlFor="country_active" className="text-sm text-navy/70">Activ</label>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="bg-gold text-navy" data-testid="country-save-btn"><Save className="w-4 h-4 mr-1" />Salvează</Button>
                      <Button type="button" variant="outline" onClick={() => { setShowCountryForm(false); setEditingCountry(null); }} data-testid="country-cancel-btn"><X className="w-4 h-4 mr-1" />Anulează</Button>
                    </div>
                  </form>
                </div>
              )}

              {/* Countries Table */}
              {geoLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-white">
                      <tr className="text-left text-sm text-navy/60 border-b border-navy/10">
                        <th className="pb-3 font-medium">Nume</th>
                        <th className="pb-3 font-medium">Continent</th>
                        <th className="pb-3 font-medium">Categorie</th>
                        <th className="pb-3 font-medium">Descriere</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium text-right">Acțiuni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCountries.map(country => (
                        <tr key={country.id} className="border-b border-navy/5 last:border-0">
                          <td className="py-3 font-medium text-navy">{country.name}</td>
                          <td className="py-3 text-navy/70 text-sm">{country.continent_name}</td>
                          <td className="py-3 text-navy/70 text-sm">
                            <div className="flex flex-wrap gap-1">
                              {(country.categories || (country.category ? [country.category] : ['Circuite Externe'])).map(cat => (
                                <span key={cat} className="px-2 py-0.5 bg-gold/10 text-navy text-xs rounded-full">{cat}</span>
                              ))}
                            </div>
                          </td>
                          <td className="py-3 text-navy/70 text-sm truncate max-w-xs">{country.description || '-'}</td>
                          <td className="py-3">
                            <button onClick={() => handleToggleCountry(country.id)} className={`px-2 py-1 rounded-full text-xs font-medium ${country.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {country.is_active ? 'Activ' : 'Inactiv'}
                            </button>
                          </td>
                          <td className="py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setEditingCountry(country); setShowCountryForm(true); }}><Edit2 className="w-3 h-3" /></Button>
                              <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteCountry(country.id)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Homepage Cards Tab */}
        {activeTab === 'homepage' && (
          <div className="bg-white rounded-2xl p-6 border border-navy/10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-heading font-semibold text-xl text-navy">Carduri Homepage</h2>
              <Button
                onClick={() => { setEditingCard(null); resetCardForm(); setShowCardForm(true); }}
                className="bg-gold hover:bg-gold-hover text-navy"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Card
              </Button>
            </div>

            {/* Card Form Modal */}
            {showCardForm && (
              <div className="mb-6 p-6 bg-navy/5 rounded-xl">
                <h3 className="font-semibold text-navy mb-4">
                  {editingCard ? 'Editează Card' : 'Adaugă Card Nou'}
                </h3>
                
                {/* Card Type Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-navy mb-2">Tip card</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setCardFormData(prev => ({ ...prev, card_type: 'link' }))}
                      className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                        cardFormData.card_type === 'link' 
                          ? 'border-gold bg-gold/10' 
                          : 'border-navy/20 hover:border-navy/40'
                      }`}
                    >
                      <LinkIcon className="w-5 h-5 mx-auto mb-1 text-navy" />
                      <span className="text-sm font-medium text-navy">Card cu Link</span>
                      <p className="text-xs text-navy/60 mt-1">Link către o pagină</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setCardFormData(prev => ({ ...prev, card_type: 'packages' }))}
                      className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                        cardFormData.card_type === 'packages' 
                          ? 'border-gold bg-gold/10' 
                          : 'border-navy/20 hover:border-navy/40'
                      }`}
                    >
                      <Package className="w-5 h-5 mx-auto mb-1 text-navy" />
                      <span className="text-sm font-medium text-navy">Pachete Selectate</span>
                      <p className="text-xs text-navy/60 mt-1">Afișează pachete specifice</p>
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input 
                      value={cardFormData.title}
                      onChange={(e) => setCardFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Titlu card *" 
                      required 
                      className="px-3 py-2 border rounded-lg bg-white text-navy" 
                    />
                    <input 
                      type="number"
                      value={cardFormData.display_order}
                      onChange={(e) => setCardFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                      placeholder="Ordine afișare" 
                      className="px-3 py-2 border rounded-lg bg-white text-navy" 
                    />
                  </div>
                  
                  {/* SEO Slug field */}
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <label className="block text-xs font-medium text-blue-700 mb-1">
                      SEO URL Slug (generat automat din titlu dacă e gol)
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-blue-600">/showcase/</span>
                      <input 
                        value={cardFormData.slug}
                        onChange={(e) => setCardFormData(prev => ({ 
                          ...prev, 
                          slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-')
                        }))}
                        placeholder="ex: revelion-2027" 
                        className="flex-1 px-3 py-1.5 border rounded-lg bg-white text-navy text-sm" 
                      />
                    </div>
                  </div>
                  
                  <textarea 
                    value={cardFormData.description}
                    onChange={(e) => setCardFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Descriere" 
                    rows={2} 
                    className="w-full px-3 py-2 border rounded-lg bg-white text-navy" 
                  />
                  <input 
                    value={cardFormData.image_url}
                    onChange={(e) => setCardFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="URL imagine *" 
                    required 
                    className="w-full px-3 py-2 border rounded-lg bg-white text-navy" 
                  />
                  
                  {/* Link card fields */}
                  {cardFormData.card_type === 'link' && (
                    <div className="grid grid-cols-2 gap-3">
                      <input 
                        value={cardFormData.link_url}
                        onChange={(e) => setCardFormData(prev => ({ ...prev, link_url: e.target.value }))}
                        placeholder="Link destinație (ex: /circuite) *" 
                        required 
                        className="px-3 py-2 border rounded-lg bg-white text-navy" 
                      />
                      <input 
                        value={cardFormData.link_text}
                        onChange={(e) => setCardFormData(prev => ({ ...prev, link_text: e.target.value }))}
                        placeholder="Text buton" 
                        className="px-3 py-2 border rounded-lg bg-white text-navy" 
                      />
                    </div>
                  )}
                  
                  {/* Package selection for package showcase cards */}
                  {cardFormData.card_type === 'packages' && (
                    <div className="bg-white rounded-lg p-4 border border-navy/10">
                      <label className="block text-sm font-medium text-navy mb-2">
                        Selectează pachete pentru acest card ({cardFormData.selected_package_ids.length} selectate)
                      </label>
                      
                      {/* Search packages */}
                      <div className="relative mb-3">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
                        <input
                          type="text"
                          value={packageSearchQuery}
                          onChange={(e) => {
                            setPackageSearchQuery(e.target.value);
                            searchPackagesForCard(e.target.value);
                          }}
                          placeholder="Caută pachete după titlu, țară sau operator..."
                          className="w-full pl-10 pr-4 py-2 border rounded-lg bg-white text-navy"
                        />
                        {packageSearchLoading && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40 animate-spin" />
                        )}
                      </div>
                      
                      {/* Search results */}
                      {packageSearchResults.length > 0 && (
                        <div className="max-h-48 overflow-y-auto border rounded-lg divide-y divide-navy/10 mb-3">
                          {packageSearchResults.map(pkg => (
                            <div 
                              key={pkg.id}
                              onClick={() => togglePackageInCard(pkg.id)}
                              className={`flex items-center gap-3 p-2 cursor-pointer hover:bg-navy/5 ${
                                cardFormData.selected_package_ids.includes(pkg.id) ? 'bg-gold/10' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={cardFormData.selected_package_ids.includes(pkg.id)}
                                onChange={() => {}}
                                className="w-4 h-4"
                              />
                              <img src={pkg.image_url} alt="" className="w-12 h-8 object-cover rounded" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-navy truncate">{pkg.title}</p>
                                <p className="text-xs text-navy/60">{pkg.operator} • {pkg.price}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Selected packages preview */}
                      {cardFormData.selected_package_ids.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {cardFormData.selected_package_ids.map(id => {
                            const pkg = packageSearchResults.find(p => p.id === id) || packages.find(p => p.id === id);
                            return (
                              <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-gold/20 rounded-full text-xs text-navy">
                                {pkg?.title?.slice(0, 20) || id.slice(0, 8)}...
                                <button 
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); togglePackageInCard(id); }}
                                  className="hover:text-red-600"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="card_active" 
                      checked={cardFormData.is_active}
                      onChange={(e) => setCardFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="w-4 h-4" 
                    />
                    <label htmlFor="card_active" className="text-sm text-navy/70">Activ</label>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      type="button"
                      onClick={() => handleSaveHomepageCard(cardFormData)}
                      className="bg-gold text-navy"
                    >
                      <Save className="w-4 h-4 mr-1" />Salvează
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => { setShowCardForm(false); setEditingCard(null); resetCardForm(); }}
                    >
                      <X className="w-4 h-4 mr-1" />Anulează
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Cards Table */}
            {homepageCardsLoading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
              </div>
            ) : homepageCards.length === 0 ? (
              <div className="text-center py-8 text-navy/50">
                Nu există carduri. Adaugă primul card pentru homepage.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-navy/60 border-b border-navy/10">
                      <th className="pb-3 font-medium">Imagine</th>
                      <th className="pb-3 font-medium">Titlu</th>
                      <th className="pb-3 font-medium">Tip</th>
                      <th className="pb-3 font-medium">Link/Pachete</th>
                      <th className="pb-3 font-medium">Ordine</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium text-right">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {homepageCards.map(card => (
                      <tr key={card.id} className="border-b border-navy/5 last:border-0">
                        <td className="py-3">
                          <img src={card.image_url} alt={card.title} className="w-16 h-12 object-cover rounded-lg" />
                        </td>
                        <td className="py-3">
                          <div className="font-medium text-navy">{card.title}</div>
                          <div className="text-xs text-navy/50 truncate max-w-xs">{card.description}</div>
                        </td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            card.card_type === 'packages' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {card.card_type === 'packages' ? `📦 ${(card.selected_package_ids || []).length} pachete` : '🔗 Link'}
                          </span>
                        </td>
                        <td className="py-3 text-navy/70 text-sm">
                          {card.card_type === 'packages' 
                            ? `${(card.selected_package_ids || []).length} pachete selectate`
                            : card.link_url
                          }
                        </td>
                        <td className="py-3 text-navy/70">{card.display_order}</td>
                        <td className="py-3">
                          <button
                            onClick={() => handleToggleHomepageCard(card.id)}
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              card.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {card.is_active ? 'Activ' : 'Inactiv'}
                          </button>
                        </td>
                        <td className="py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => openCardFormForEdit(card)}>
                              <Edit2 className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteHomepageCard(card.id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Operators Tab */}
        {activeTab === 'operators' && (
          <div className="space-y-6" data-testid="operators-section">
            {/* Pending Packages Section */}
            {pendingPackages.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Pachete în așteptare ({pendingPackages.length})
                </h3>
                <div className="space-y-3">
                  {pendingPackages.map(pkg => (
                    <div key={pkg.id} className="bg-white rounded-lg p-4 flex items-center gap-4 border border-amber-100" data-testid={`pending-${pkg.id}`}>
                      <img src={pkg.image_url} alt="" className="w-16 h-12 rounded object-cover" onError={e => { e.target.style.display='none'; }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-navy truncate">{pkg.title}</div>
                        <div className="text-xs text-navy/50">
                          {pkg.creator_info?.operator_name || 'Necunoscut'} &middot; {pkg.category} &middot; {pkg.price}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleApprovePackage(pkg.id, 'approve')}
                          className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 flex items-center gap-1" data-testid={`approve-${pkg.id}`}>
                          <CheckCircle className="w-3.5 h-3.5" /> Aprobă
                        </button>
                        <button onClick={() => handleApprovePackage(pkg.id, 'reject')}
                          className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 flex items-center gap-1" data-testid={`reject-${pkg.id}`}>
                          <XCircle className="w-3.5 h-3.5" /> Respinge
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Create operator form */}
            {showAccountForm ? (
              <div className="bg-white rounded-xl border border-navy/10 p-6 space-y-4" data-testid="create-operator-form">
                <h3 className="text-lg font-bold text-navy">Cont Operator Nou</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1">Nume persoană *</label>
                    <input type="text" value={newOperator.name} onChange={e => setNewOperator(p => ({...p, name: e.target.value}))}
                      className="w-full px-3 py-2 border border-navy/20 rounded-lg text-navy" placeholder="Ion Popescu" data-testid="op-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1">Nume companie *</label>
                    <input type="text" value={newOperator.operator_name} onChange={e => setNewOperator(p => ({...p, operator_name: e.target.value}))}
                      className="w-full px-3 py-2 border border-navy/20 rounded-lg text-navy" placeholder="EuroTravel SRL" data-testid="op-company" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1">Email *</label>
                    <input type="email" value={newOperator.email} onChange={e => setNewOperator(p => ({...p, email: e.target.value}))}
                      className="w-full px-3 py-2 border border-navy/20 rounded-lg text-navy" placeholder="operator@firma.ro" data-testid="op-email" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy mb-1">Parolă *</label>
                    <input type="text" value={newOperator.password} onChange={e => setNewOperator(p => ({...p, password: e.target.value}))}
                      className="w-full px-3 py-2 border border-navy/20 rounded-lg text-navy" placeholder="Minim 8 caractere" data-testid="op-password" />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={newOperator.can_publish_directly}
                    onChange={e => setNewOperator(p => ({...p, can_publish_directly: e.target.checked}))}
                    className="rounded border-navy/20" data-testid="op-direct-publish" />
                  <span className="text-sm text-navy">Publicare directă (fără aprobare)</span>
                </label>
                <div className="flex justify-end gap-3 pt-2">
                  <Button onClick={() => setShowAccountForm(false)} className="bg-navy/10 text-navy hover:bg-navy/20">Anulează</Button>
                  <Button onClick={handleCreateOperator} className="bg-gold text-navy hover:bg-gold/90" data-testid="op-save">
                    Creează cont operator
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => setShowAccountForm(true)} className="bg-gold text-navy hover:bg-gold/90" data-testid="add-operator-btn">
                <Plus className="w-4 h-4 mr-2" /> Adaugă Operator Nou
              </Button>
            )}

            {/* Operators list */}
            <div className="bg-white rounded-xl border border-navy/10" data-testid="operators-list">
              <div className="px-6 py-4 border-b border-navy/10">
                <h3 className="text-lg font-bold text-navy">Conturi Operatori ({operatorAccounts.length})</h3>
              </div>
              {operatorsLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-navy/30" /></div>
              ) : operatorAccounts.length === 0 ? (
                <div className="py-12 text-center text-navy/40">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>Nu există operatori încă.</p>
                </div>
              ) : (
                <div className="divide-y divide-navy/5">
                  {operatorAccounts.map(op => (
                    <div key={op.id} className="px-6 py-4 flex items-center gap-4" data-testid={`operator-${op.id}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${op.is_active !== false ? 'bg-gold/20 text-gold' : 'bg-red-100 text-red-500'}`}>
                        {(op.operator_name || op.name || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-navy">{op.operator_name}</div>
                        <div className="text-xs text-navy/50">{op.email} &middot; {op.name}</div>
                      </div>
                      <div className="text-xs text-center">
                        <div className="font-bold text-navy">{op.package_counts?.total || 0}</div>
                        <div className="text-navy/40">programe</div>
                      </div>
                      {op.package_counts?.pending > 0 && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                          {op.package_counts.pending} pending
                        </span>
                      )}
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                          <input type="checkbox" checked={op.can_publish_directly || false}
                            onChange={() => handleToggleOperatorPublish(op.id, op.can_publish_directly)}
                            className="rounded border-navy/20" />
                          <span className="text-navy/60">Direct</span>
                        </label>
                        <button onClick={() => handleToggleOperatorActive(op.id, op.is_active !== false)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                            op.is_active !== false ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-600 hover:bg-red-200'
                          }`} data-testid={`toggle-active-${op.id}`}>
                          {op.is_active !== false ? 'Activ' : 'Inactiv'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Event Categories Tab */}
        {activeTab === 'event-categories' && (
          <EventCategoriesManager secureFetch={secureFetch} />
        )}
      </div>
    </div>
  );
}
